<template>
  <div class="reset-password-page">
    <div v-if="status === 'loading'" class="loading-wrapper">
      <div class="spinner"></div>
      <p>{{ t('account.personal_data.btn.saving') }}</p>
    </div>

    <div v-else-if="status === 'valid'" class="reset-content">
      <h1 class="form-title">{{ t('account.password_modal.title') }}</h1>

      <form class="form-container" @submit.prevent="handleSubmit">
        <!-- New Password Input -->
        <div class="form-group">
          <label for="new-password" class="form-label">{{ t('account.password_modal.new_pass') }}</label>
          <div class="password-input-wrapper">
            <input
              id="new-password"
              name="password"
              v-model="formData.newPassword"
              :type="showNewPassword ? 'text' : 'password'"
              class="form-control"
              :placeholder="t('account.password_modal.req_min_chars')"
            />
            <button type="button" class="password-toggle" @click="showNewPassword = !showNewPassword">
              <svg v-if="!showNewPassword" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
            </button>
          </div>
        </div>

        <!-- Password Requirements Indicator -->
        <div class="requirements-indicator">
          <p class="requirements-title">{{ t('account.password_modal.requirements_title') }}</p>
          <ul class="requirements-list">
            <li :class="{ met: validation.requirements.minLength.value }">
              ✓ {{ t('account.password_modal.req_min_chars') }}
            </li>
            <li :class="{ met: validation.requirements.hasUppercase.value }">
              ✓ {{ t('account.password_modal.req_uppercase') }}
            </li>
            <li :class="{ met: validation.requirements.hasNumber.value }">
              ✓ {{ t('account.password_modal.req_number') }}
            </li>
            <li :class="{ met: validation.requirements.hasSpecial.value }">
              ✓ {{ t('account.password_modal.req_special') }}
            </li>
          </ul>
        </div>

        <!-- Confirm Password Input -->
        <div class="form-group">
          <label for="confirm-password" class="form-label">{{ t('account.password_modal.confirm_pass') }}</label>
          <div class="password-input-wrapper">
            <input
              id="confirm-password"
              name="confirm_password"
              v-model="formData.confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              class="form-control"
              :placeholder="t('account.password_modal.confirm_pass')"
            />
            <button type="button" class="password-toggle" @click="showConfirmPassword = !showConfirmPassword">
              <svg v-if="!showConfirmPassword" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
            </button>
          </div>
        </div>

        <!-- Error/Success Alerts -->
        <div v-if="apiError" class="alert alert-danger">{{ t('apiCodes.' + apiError) }}</div>
        <div v-if="successMessage" class="alert alert-success">{{ t('apiCodes.' + successMessage) }}</div>

        <!-- Submit Button -->
        <button
          type="submit"
          class="btn-submit"
          :disabled="isLoading || successMessage || !validation.isValid.value || formData.newPassword !== formData.confirmPassword"
        >
          <span v-if="isLoading" class="spinner"></span>
          {{ isLoading ? t('account.personal_data.btn.saving') : t('account.personal_data.btn.save') }}
        </button>

        <!-- Legal notice for reCAPTCHA -->
        <div class="recaptcha-notice" v-html="t('form.recaptcha_notice', { 
          privacy: `<a href='https://policies.google.com/privacy' target='_blank'>${t('form.google_privacy')}</a>`, 
          terms: `<a href='https://policies.google.com/terms' target='_blank'>${t('form.google_terms')}</a>` 
        })"></div>
      </form>
    </div>
    
    <div v-else-if="status === 'invalid'" class="error-editorial-wrapper">
      <div class="error-editorial-content">
        <p class="error-editorial-message">{{ t('apiCodes.' + errorMessage) }}</p>
        <router-link to="/login" class="btn-back-login">
          {{ t('auth.forgot_success.btn_login') }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useConfigStore } from '@/stores/configStore'
import { usePasswordValidation } from '@/composables/usePasswordValidation'
import { useLanguage } from '@/composables/useLanguage'

const route = useRoute()
const router = useRouter()
const { validateResetToken, resetPassword, isLoading } = useAuth()
const configStore = useConfigStore()
const { t } = useLanguage()

const token = ref(route.query.token || '')
const status = ref('loading')
const errorMessage = ref('')
const apiError = ref('')
const successMessage = ref('')
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

const formData = ref({
  newPassword: '',
  confirmPassword: ''
})

const validation = usePasswordValidation(computed(() => formData.value.newPassword))

const validateToken = async () => {
  const tokenParam = route.query.token
  if (!tokenParam) {
    status.value = 'invalid'
    errorMessage.value = 'AUTH_TOKEN_MISSING'
    return
  }
  token.value = tokenParam
  try {
    await validateResetToken(tokenParam)
    status.value = 'valid'
  } catch (err) {
    status.value = 'invalid'
    errorMessage.value = err?.response?.data?.code || 'AUTH_TOKEN_EXPIRED'
  }
}

const generateRecaptchaToken = async () => {
  if (!configStore.recaptchaSiteKey) return ''
  
  const waitForRecaptcha = () => {
    return new Promise((resolve) => {
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        resolve(true)
        return
      }
      
      let attempts = 0
      const interval = setInterval(() => {
        attempts++
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          clearInterval(interval)
          resolve(true)
        } else if (attempts > 50) {
          clearInterval(interval)
          resolve(false)
        }
      }, 100)
    })
  }

  const isRecaptchaReady = await waitForRecaptcha()
  if (!isRecaptchaReady) return ''

  return await new Promise((resolve) => {
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(configStore.recaptchaSiteKey, { action: 'reset_password' })
        resolve(token)
      } catch (e) {
        console.error('[RECAPTCHA] Execute error:', e)
        resolve('')
      }
    })
  })
}

const handleSubmit = async () => {
  if (!validation.isValid.value || formData.value.newPassword !== formData.value.confirmPassword) return
  apiError.value = ''
  successMessage.value = ''
  try {
    const recaptchaToken = await generateRecaptchaToken()
    if (!recaptchaToken) {
      apiError.value = 'AUTH_RECAPTCHA_REQUIRED'
      return
    }
    const result = await resetPassword(token.value, formData.value.newPassword, formData.value.confirmPassword, recaptchaToken)
    if (result && result.success) {
      successMessage.value = 'AUTH_RESET_SUCCESS'
      setTimeout(() => {
        router.push('/login')
      }, 5000)
    } else {
      const code = result?.code || 'SYSTEM_ERROR'
      apiError.value = code
    }
  } catch (err) {
    const code = err?.response?.data?.code || 'SYSTEM_ERROR'
    apiError.value = code
  }
}

onMounted(() => {
  validateToken()
})
</script>

<style scoped>
.reset-password-page {
  width: 100%;
}

.loading-wrapper {
  text-align: center;
  padding: 40px 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid var(--teal);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin { to { transform: rotate(360deg); } }

.form-title {
  text-align: center;
  font-size: 18px; /* Ridotto per maggiore eleganza */
  font-weight: 900;
  margin-bottom: 32px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #000;
}

.form-group { margin-bottom: 25px; }

.form-label {
  display: block;
  font-size: 11px;
  font-weight: bold;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.form-control {
  width: 100%;
  padding: 14px 15px;
  font-size: 14px;
  border: 1px solid #e0e0e0;
  background-color: #ffffff;
  border-radius: 4px;
}

.password-input-wrapper { position: relative; }

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #999;
  cursor: pointer;
}

.requirements-indicator {
  margin: 30px 0;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 4px;
}

.requirements-title {
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  margin-bottom: 15px;
  letter-spacing: 1px;
}

.requirements-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.requirements-list li {
  font-size: 10px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.requirements-list li.met {
  color: var(--teal);
  font-weight: 900;
}

.btn-submit {
  width: 100%;
  padding: 18px;
  background-color: #000;
  color: white;
  border: none;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 2px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.btn-submit:hover:not(:disabled) {
  background-color: #1a1a1a;
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
}

.btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }

.alert {
  padding: 14px;
  margin-bottom: 24px;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid #ff0000;
  color: #ff0000;
  text-transform: uppercase;
  letter-spacing: 1px;
  text-align: center;
  background: rgba(255, 0, 0, 0.05);
}

.alert-success {
  border-color: var(--teal);
  color: var(--teal);
  background: rgba(91, 166, 160, 0.05);
}

.error-editorial-wrapper { text-align: center; padding: 40px 0; }
.error-editorial-message { color: #ff0000; font-weight: 900; margin-bottom: 30px; text-transform: uppercase; letter-spacing: 1px; }
.btn-back-login { 
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 18px; 
  background: #000; 
  color: white; 
  text-decoration: none; 
  font-weight: 900; 
  text-transform: uppercase;
  letter-spacing: 2px;
  border-radius: 4px;
}
</style>
